import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { type Psychologist } from '@avanta/shared/types/psychologists';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight } from 'lucide-react';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AppointmentsReservePayload,
  type StepProps,
} from 'pages/Appointments/AppointmentsReserve/types';
import { AppointmentSteps } from 'types/appointments';
import { PSYCHOLOGISTS_KEY_PREFIX } from '..';
import { BackButton } from '../../../../ui/BackButton';
import { PsychoCheckbox } from '../../../../ui/PsychoCheckbox';
import { SessionSummary } from '../../../../ui/SessionSummary';
import { StepSection } from '../../../../ui/StepSection';
import { useFetchTopPsychologists } from '../../useFetchTopPsychologists';
import { PsychologistStepSkeleton } from '../PsychologistStepSkeleton';

export const MobileView = ({
  handleNext,
  handleBack,
  handleOnCheckedChange,
  nextStepName,
}: StepProps & {
  handleOnCheckedChange: (checked: boolean, psychologist: Psychologist) => void;
}): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { watch } = useFormContext();
  const psychologist = watch(AppointmentsReservePayload.psychologist);
  const {
    psychologists: filteredPsychologists,
    isLoading,
    isFetching,
  } = useFetchTopPsychologists();
  const isLoadingPsychologists = isLoading || isFetching;

  const layoutRootRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isAtBottom } = useHasVerticalOverflow(
    layoutRootRef,
    'nearestScrollParent',
    filteredPsychologists
  );
  const showBottomShadow = hasOverflow && !isAtBottom;

  const psychologists: { psychologist: Psychologist }[] = filteredPsychologists ?? [];
  const nextStepLabel =
    nextStepName === AppointmentSteps.payment ? 'Steps.continueWithPayment' : 'next';

  return (
    <div ref={layoutRootRef} className="am-lg:hidden relative flex flex-col gap-8">
      <div className="flex flex-col flex-1 gap-8 px-1 pb-1">
        <SessionSummary title={t(`${PSYCHOLOGISTS_KEY_PREFIX}yourSelection.title`)} showLanguages />
        <StepSection
          title={t(`${PSYCHOLOGISTS_KEY_PREFIX}availableProfessionals.title`)}
          className="w-full flex flex-col gap-4"
          fullWidth
        >
          {isLoadingPsychologists ? (
            <PsychologistStepSkeleton />
          ) : (
            psychologists.map(({ psychologist: item }) => (
              <PsychoCheckbox
                key={item.id}
                psychologist={item}
                checked={item.id === psychologist?.id}
                onCheckedChange={(checked: boolean) => handleOnCheckedChange(checked, item)}
              />
            ))
          )}
        </StepSection>
      </div>
      <div
        className={cn(
          'flex justify-between gap-4 items-center w-full h-14',
          psychologist && 'sticky bottom-0 left-0 right-0 bg-white pt-1.5 pb-0',
          isAtBottom && 'pb-4 h-auto pt-0',
          showBottomShadow && psychologist && 'shadow-mobile-menu-top justify-center -mx-4'
        )}
      >
        {(!psychologist || isAtBottom) && <BackButton handleBack={handleBack} />}
        <Button
          type="button"
          variant="primary"
          className={cn('flex-1', showBottomShadow && psychologist && 'w-9/10 flex-none')}
          size={psychologist && !isAtBottom ? 'md' : 'lg'}
          icon="trailing"
          onClick={handleNext}
          disabled={!psychologist}
        >
          {t(`AppointmentsReserve.${nextStepLabel}`)}
          <ChevronRight className="size-5" />
        </Button>
      </div>
    </div>
  );
};
